// Copyright (c) 2015-present, salesforce.com, inc. All rights reserved

// # Homepage Card

// ## Dependencies
import { Card } from '@salesforce/design-system-react';
// ### classNames
// [github.com/JedWatson/classnames](https://github.com/JedWatson/classnames)
// A simple javascript utility for conditionally joining classNames together.
import classNames from 'classnames';
import throttle from 'lodash.throttle';

// ### React

import React from 'react';
import PropTypes from 'prop-types';

import SubHeader from './sub-header';

// eslint-disable-next-line react/no-deprecated
class HomepageCard extends React.Component {
  constructor(props) {
    super(props);

    this.determineIfFooterIsNeeded = this.determineIfFooterIsNeeded.bind(this);
    this.state = {
      showFooter: false,
    };
  }

  componentDidMount() {
    this.onResizeThrottled = throttle(this.determineIfFooterIsNeeded, 10);
    window.addEventListener('resize', this.onResizeThrottled); // eslint-disable-line no-undef

    this.determineIfFooterIsNeeded();
  }

  componentDidUpdate() {
    this.determineIfFooterIsNeeded();
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.onResizeThrottled); // eslint-disable-line no-undef
  }

  determineIfFooterIsNeeded() {
    let showFooter;
    if (this.props.forceShowFooter && this.props.forceShowFooter === true) {
      showFooter = true;
    } else {
      showFooter = this.bodyWrapper.parentNode.scrollHeight > this.bodyWrapper.parentNode.clientHeight;
    }

    if (this.state.showFooter !== showFooter) {
      this.setState({ showFooter });
    }
  }

  render() {
    const { className, children, footer, heading, height, icon, subHeader, ...rest } = this.props;

    return (
      <Card
        bodyClassName="slds-grow slds-scrollable--y"
        className={classNames(
          'slds-grid slds-grid--vertical slds-card--narrow slds-theme--default',
          { 'slds-p-bottom--large': !this.state.showFooter },
          className
        )}
        footer={this.state.showFooter ? footer : null}
        heading={heading}
        icon={icon}
        header={subHeader ? <SubHeader heading={heading} icon={icon} subHeader={subHeader} /> : undefined}
        style={{ height }}
        {...rest}
      >
        <div
          className="slds-card__body--inner"
          ref={(div) => {
            this.bodyWrapper = div;
          }}
        >
          {children}
        </div>
      </Card>
    );
  }
}
HomepageCard.displayName = 'HOMEPAGE_CARD';
HomepageCard.defaultProps = {
  height: '480px',
};
// ### Prop Types
HomepageCard.propTypes = {
  /**
   * CSS classes to be added to the design-system-react Card.
   */
  className: PropTypes.string,
  /**
   * The main body of the HomepageCard.
   */
  children: PropTypes.node,
  /**
   * Footer that will be shown if Card content exceeds a certain height
   */
  footer: PropTypes.node,
  /**
   * forceShowFooter will force the footer to show
   */
  forceShowFooter: PropTypes.bool,
  /**
   * The heading is the name of the related item group.
   */
  heading: PropTypes.string.isRequired,
  /**
   * Set height of the Homepage Card.
   */
  height: PropTypes.string,
  /**
   * Icon associated with the items within the `body`.
   */
  icon: PropTypes.node,
  /**
   * Sub header node to be rendered underneath the icon and heading.
   */
  subHeader: PropTypes.node,
};

export default HomepageCard;
